/**
 * useWakeLock Hook
 * Prevents the screen from dimming or locking while active.
 *
 * Uses the Screen Wake Lock API (best-in-class, widely supported):
 * - Chrome 84+, Edge 84+, Safari 16.4+, Opera 70+
 *
 * Features:
 * - Automatically re-acquires lock when page becomes visible
 * - Fallback for browsers without Wake Lock API support
 * - Proper cleanup on unmount
 */

import { useEffect, useRef, useCallback, useState } from "react";

interface WakeLockState {
  isSupported: boolean;
  isActive: boolean;
  error: string | null;
}

export function useWakeLock(enabled: boolean = true): WakeLockState {
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const [state, setState] = useState<WakeLockState>({
    isSupported: typeof navigator !== "undefined" && "wakeLock" in navigator,
    isActive: false,
    error: null,
  });

  // Fallback video element for browsers without Wake Lock API
  const fallbackVideoRef = useRef<HTMLVideoElement | null>(null);

  const requestWakeLock = useCallback(async () => {
    if (!enabled) return;

    // Check if Wake Lock API is supported
    if ("wakeLock" in navigator) {
      try {
        // Release any existing lock first
        if (wakeLockRef.current) {
          await wakeLockRef.current.release();
          wakeLockRef.current = null;
        }

        // Request a new wake lock
        wakeLockRef.current = await navigator.wakeLock.request("screen");

        setState((prev) => ({
          ...prev,
          isActive: true,
          error: null,
        }));

        // Listen for release event (e.g., when tab becomes hidden)
        wakeLockRef.current.addEventListener("release", () => {
          setState((prev) => ({
            ...prev,
            isActive: false,
          }));
        });

        console.log("[WakeLock] Screen wake lock acquired");
      } catch (err) {
        const error =
          err instanceof Error ? err.message : "Failed to acquire wake lock";
        console.warn("[WakeLock] Failed to acquire wake lock:", error);
        setState((prev) => ({
          ...prev,
          isActive: false,
          error,
        }));

        // Try fallback
        startFallback();
      }
    } else {
      // Use fallback for unsupported browsers
      console.log("[WakeLock] Wake Lock API not supported, using fallback");
      startFallback();
    }
  }, [enabled]);

  const releaseWakeLock = useCallback(async () => {
    // Release native wake lock
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        console.log("[WakeLock] Screen wake lock released");
      } catch (err) {
        console.warn("[WakeLock] Error releasing wake lock:", err);
      }
    }

    // Stop fallback
    stopFallback();

    setState((prev) => ({
      ...prev,
      isActive: false,
    }));
  }, []);

  // Fallback: Use a tiny looping video to keep screen awake
  // This works on most mobile browsers that don't support Wake Lock API
  const startFallback = useCallback(() => {
    if (fallbackVideoRef.current) return;

    try {
      // Create a minimal video element
      const video = document.createElement("video");
      video.setAttribute("playsinline", "");
      video.setAttribute("muted", "");
      video.setAttribute("loop", "");
      video.style.cssText = `
        position: fixed;
        top: -9999px;
        left: -9999px;
        width: 1px;
        height: 1px;
        opacity: 0.01;
        pointer-events: none;
      `;

      // Use a tiny base64 encoded video (1x1 pixel, ~500 bytes)
      // This is a minimal WebM video that loops
      video.src =
        "data:video/webm;base64,GkXfowEAAAAAAAAfQoaBAUL3gQFC8oEEQvOBCEKChHdlYm1Ch4EEQoWBAhhTgGcBAAAAAAAB8BFNm3RALE27i1OrhBVJqWZTrIHfTbuMU6uEFlSua1OsggEuTbuMU6uEHFO7a1OsggI+7AEAAAAAAAASsIEBY1WwAQAAAAAAAP//AAAAAAAAIUmIhAAAAAABAAAAAAAAU2FsdGVkX18AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAB9DtnUBAAAAAAAAHnddwwAAAAAAi1MBAAAAAAABAAAAAAAAMhFNm3RALE27i1OrhBVJqWZTrIHfTbuMU6uEFlSua1OsggEuTbuMU6uEHFO7a1OsggI+7AEAAAAAAAASsIEBY1WwAQAAAAAAAP//";

      // Append to document
      document.body.appendChild(video);
      fallbackVideoRef.current = video;

      // Start playing
      video
        .play()
        .then(() => {
          console.log("[WakeLock] Fallback video started");
          setState((prev) => ({
            ...prev,
            isActive: true,
            error: null,
          }));
        })
        .catch((err) => {
          console.warn("[WakeLock] Fallback video failed:", err);
          // Clean up on failure
          stopFallback();
        });
    } catch (err) {
      console.warn("[WakeLock] Error creating fallback:", err);
    }
  }, []);

  const stopFallback = useCallback(() => {
    if (fallbackVideoRef.current) {
      fallbackVideoRef.current.pause();
      fallbackVideoRef.current.remove();
      fallbackVideoRef.current = null;
      console.log("[WakeLock] Fallback video stopped");
    }
  }, []);

  // Handle visibility change - re-acquire lock when page becomes visible
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Re-acquire wake lock when page becomes visible again
        requestWakeLock();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, requestWakeLock]);

  // Main effect for acquiring/releasing wake lock
  useEffect(() => {
    if (enabled) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }

    // Cleanup on unmount
    return () => {
      releaseWakeLock();
    };
  }, [enabled, requestWakeLock, releaseWakeLock]);

  return state;
}
